"use client";

import {
  DataTable,
  Notice,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { CARD, HINT, WIDGET_H2, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { BondYieldCalculatorStrings } from "@/components/tools/bond-yield-calculator";
import { useMoney } from "@/components/tools/finance/shared";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  accruedInterest,
  type BondTerms,
  bondDuration,
  bondPrice,
  cashFlows,
  couponFacts,
  currentYield,
  type Ymd,
} from "@/lib/tool-kit/accounting/bonds";

type Strings = BondYieldCalculatorStrings;

const SHOCKS = [-0.02, -0.01, 0.01, 0.02] as const;

function useDate(): (date: Ymd) => string {
  const locale = useToolLocale();
  return (date) =>
    new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeZone: "UTC",
    }).format(new Date(Date.UTC(date.y, date.m - 1, date.d)));
}

export function BondResults({
  terms,
  y,
  face,
  currency,
  strings,
}: {
  terms: BondTerms;
  y: number;
  face: number;
  currency: string;
  strings: Strings;
}) {
  const fmt = useToolNumber();
  const money = useMoney(currency);
  const date = useDate();
  const facts = couponFacts(
    terms.settlement,
    terms.maturity,
    terms.frequency,
    terms.basis
  );
  const clean = bondPrice(terms, y);
  const accrued = accruedInterest(terms);
  const duration = bondDuration(terms, y);
  const flows = cashFlows(terms, y);
  const num = (v: number, max = 4) => fmt(v, { maximumFractionDigits: max });
  const pct = (v: number, max = 4) =>
    `${fmt(v * 100, { maximumFractionDigits: max })}%`;
  const per = (v: number) => money((v / 100) * face);
  const shocks = SHOCKS.filter((s) => y + s > -terms.frequency).map((s) => {
    const price = bondPrice(terms, y + s);
    return [
      `${s > 0 ? "+" : "−"}${fmt(Math.abs(s) * 100)}`,
      pct(y + s, 3),
      num(price, 3),
      pct(price / clean - 1, 2),
      pct(-duration.modified * s, 2),
    ];
  });
  return (
    <section aria-live="polite" className="space-y-5">
      <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
      <StatGrid>
        <StatCard emphasis label={strings.cleanPrice} value={num(clean, 4)} />
        <StatCard label={strings.ytm} value={pct(y, 4)} />
        <StatCard
          label={strings.currentYield}
          value={pct(currentYield(terms.rate, clean), 4)}
        />
        <StatCard label={strings.macaulay} value={num(duration.macaulay, 4)} />
        <StatCard label={strings.modified} value={num(duration.modified, 4)} />
        <StatCard label={strings.dirtyPrice} value={num(clean + accrued, 4)} />
      </StatGrid>
      <p className={HINT}>{strings.perHundred}</p>
      <h3 className={WIDGET_H3}>
        {t(strings.faceHeading, { face: money(face) })}
      </h3>
      <DataTable
        head={[strings.itemColumn, strings.amountColumn]}
        rows={[
          [strings.cleanAmount, per(clean)],
          [strings.accruedAmount, per(accrued)],
          [strings.invoiceAmount, per(clean + accrued)],
          [strings.couponAmount, money((face * terms.rate) / terms.frequency)],
        ]}
      />
      <div className={`${CARD} space-y-1`}>
        <h3 className={WIDGET_H3}>{strings.workingHeading}</h3>
        <p className="font-mono text-muted-foreground text-sm leading-relaxed">
          {t(strings.workFacts, {
            n: fmt(facts.n),
            a: num(facts.a, 2),
            e: num(facts.e, 2),
            dsc: num(facts.dsc, 2),
            previous: date(facts.previous),
            next: date(facts.next),
          })}
        </p>
        <p className="font-mono text-muted-foreground text-sm leading-relaxed">
          {t(strings.workAccrued, {
            coupon: num((100 * terms.rate) / terms.frequency, 4),
            a: num(facts.a, 2),
            e: num(facts.e, 2),
            accrued: num(accrued, 4),
          })}
        </p>
        <p className="font-mono text-muted-foreground text-sm leading-relaxed">
          {t(strings.workPrice, {
            dirty: num(clean + accrued, 4),
            accrued: num(accrued, 4),
            clean: num(clean, 4),
          })}
        </p>
      </div>
      {facts.n === 1 ? <Notice>{strings.lastPeriodNote}</Notice> : null}
      <h3 className={WIDGET_H3}>{strings.shockHeading}</h3>
      <DataTable
        head={[
          strings.shockChange,
          strings.shockYield,
          strings.shockPrice,
          strings.shockActual,
          strings.shockEstimate,
        ]}
        rows={shocks}
      />
      <h3 className={WIDGET_H3}>{strings.flowsHeading}</h3>
      <DataTable
        head={[
          strings.flowNumber,
          strings.flowDate,
          strings.flowAmount,
          strings.flowYears,
          strings.flowPv,
          strings.flowWeighted,
        ]}
        rows={flows.map((row) => [
          row.k,
          date(row.date),
          num(row.cashFlow, 4),
          num(row.years, 4),
          num(row.presentValue, 4),
          num(row.years * row.presentValue, 4),
        ])}
      />
    </section>
  );
}
